
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart3, Users, FileText, Calendar, DollarSign, TrendingUp, Mail, BookOpen, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";

interface Statistics {
  totalRegistrations: number;
  pendingRegistrations: number;
  approvedRegistrations: number;
  totalStudents: number;
  totalLecturers: number;
  totalGroups: number;
  totalLessons: number;
  activeSubscribers: number;
  monthlyRevenue: number;
  completedLessons: number;
  totalFinancialRecords: number;
  totalProfiles: number;
  totalCourses: number;
  activeCourses: number;
  totalFaculties: number;
  activeFaculties: number;
  // Financial statistics from financial_records
  pendingSalaries: number;
  approvedSalaries: number;
  pendingTuition: number;
  approvedTuition: number;
  lecturersAwaitingPayment: number;
  studentsWithDebt: number;
}

export const StatisticsSection = () => {
  const [stats, setStats] = useState<Statistics>({
    totalRegistrations: 0,
    pendingRegistrations: 0,
    approvedRegistrations: 0,
    totalStudents: 0,
    totalLecturers: 0,
    totalGroups: 0,
    totalLessons: 0,
    activeSubscribers: 0,
    monthlyRevenue: 0,
    completedLessons: 0,
    totalFinancialRecords: 0,
    totalProfiles: 0,
    totalCourses: 0,
    activeCourses: 0,
    totalFaculties: 0,
    activeFaculties: 0,
    pendingSalaries: 0,
    approvedSalaries: 0,
    pendingTuition: 0,
    approvedTuition: 0,
    lecturersAwaitingPayment: 0,
    studentsWithDebt: 0
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchStatistics = async () => {
    try {
      setLoading(true);

      // Clear current stats to show loading state
      setStats({
        totalRegistrations: 0,
        pendingRegistrations: 0,
        approvedRegistrations: 0,
        totalStudents: 0,
        totalLecturers: 0,
        totalGroups: 0,
        totalLessons: 0,
        activeSubscribers: 0,
        monthlyRevenue: 0,
        completedLessons: 0,
        totalFinancialRecords: 0,
        totalProfiles: 0,
        totalCourses: 0,
        activeCourses: 0,
        totalFaculties: 0,
        activeFaculties: 0,
        pendingSalaries: 0,
        approvedSalaries: 0,
        pendingTuition: 0,
        approvedTuition: 0,
        lecturersAwaitingPayment: 0,
        studentsWithDebt: 0
      });

      // Fetch all data in parallel with error handling for each query
      const [
        registrationsResult,
        pendingRegistrationsResult,
        approvedRegistrationsResult,
        studentsResult,
        lecturersResult,
        groupsResult,
        lessonsResult,
        completedLessonsResult,
        subscribersResult,
        profilesResult,
        coursesResult,
        activeCoursesResult,
        facultiesResult,
        activeFacultiesResult,
        financialResult
      ] = await Promise.allSettled([
        // Registrations
        supabase.from('registrations').select('*', { count: 'exact', head: true }),
        supabase.from('registrations').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('registrations').select('*', { count: 'exact', head: true }).eq('status', 'approved'),
        // Users
        supabase.from('user_roles').select('*', { count: 'exact', head: true }).eq('role', 'student'),
        supabase.from('user_roles').select('*', { count: 'exact', head: true }).eq('role', 'lecturer'),
        // Groups and lessons
        supabase.from('groups').select('*', { count: 'exact', head: true }),
        supabase.from('lessons').select('*', { count: 'exact', head: true }),
        supabase.from('lessons').select('*', { count: 'exact', head: true }).eq('status', 'completed'),
        // Newsletter
        supabase.from('newsletter_subscribers').select('*', { count: 'exact', head: true }).eq('is_active', true),
        // Profiles
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        // Courses
        supabase.from('courses').select('*', { count: 'exact', head: true }),
        supabase.from('courses').select('*', { count: 'exact', head: true }).eq('is_active', true),
        // Faculties
        supabase.from('faculties').select('*', { count: 'exact', head: true }),
        supabase.from('faculties').select('*', { count: 'exact', head: true }).eq('is_active', true),
        // Financial statistics
        supabase.rpc('get_financial_statistics')
      ]);

      // Helper function to extract count from result
      const getCount = (result: PromiseSettledResult<any>): number => {
        if (result.status === 'fulfilled' && result.value.count !== null) {
          return result.value.count;
        }
        return 0;
      };

      // Helper function to extract financial data
      const getFinancialData = (result: PromiseSettledResult<any>) => {
        if (result.status === 'fulfilled' && result.value.data?.[0]) {
          return result.value.data[0];
        }
        return {
          total_pending_salaries: 0,
          total_approved_salaries: 0,
          total_pending_tuition: 0,
          total_approved_tuition: 0,
          lecturers_awaiting_payment: 0,
          students_with_debt: 0
        };
      };

      // Extract results with error handling
      const totalRegistrations = getCount(registrationsResult);
      const pendingRegistrations = getCount(pendingRegistrationsResult);
      const approvedRegistrations = getCount(approvedRegistrationsResult);
      const totalStudents = getCount(studentsResult);
      const totalLecturers = getCount(lecturersResult);
      const totalGroups = getCount(groupsResult);
      const totalLessons = getCount(lessonsResult);
      const completedLessons = getCount(completedLessonsResult);
      const activeSubscribers = getCount(subscribersResult);
      const totalProfiles = getCount(profilesResult);
      const totalCourses = getCount(coursesResult);
      const activeCourses = getCount(activeCoursesResult);
      const totalFaculties = getCount(facultiesResult);
      const activeFaculties = getCount(activeFacultiesResult);
      const financial = getFinancialData(financialResult);

      // Get total financial records count separately
      const financialRecordsResult = await supabase
        .from('financial_records')
        .select('*', { count: 'exact', head: true });
      
      const totalFinancialRecords = financialRecordsResult.count || 0;

      // Calculate monthly revenue from approved tuition payments
      const monthlyRevenue = financial.total_approved_tuition || 0;

      setStats({
        totalRegistrations,
        pendingRegistrations,
        approvedRegistrations,
        totalStudents,
        totalLecturers,
        totalGroups,
        totalLessons,
        activeSubscribers,
        monthlyRevenue,
        completedLessons,
        totalFinancialRecords,
        totalProfiles,
        totalCourses,
        activeCourses,
        totalFaculties,
        activeFaculties,
        pendingSalaries: financial.total_pending_salaries || 0,
        approvedSalaries: financial.total_approved_salaries || 0,
        pendingTuition: financial.total_pending_tuition || 0,
        approvedTuition: financial.total_approved_tuition || 0,
        lecturersAwaitingPayment: financial.lecturers_awaiting_payment || 0,
        studentsWithDebt: financial.students_with_debt || 0
      });

      toast({
        title: "Հաջողություն",
        description: "Վիճակագրությունը թարմացվեց",
        variant: "default"
      });
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել վիճակագրությունը",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
    
    // Set up real-time subscription for financial records changes
    const channel = supabase
      .channel('statistics-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'financial_records'
        },
        () => {
          console.log('Financial records changed, refreshing statistics...');
          fetchStatistics();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payments'
        },
        () => {
          console.log('Payments changed, refreshing statistics...');
          fetchStatistics();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'registrations'
        },
        () => {
          console.log('Registrations changed, refreshing statistics...');
          fetchStatistics();
        }
      )
      .subscribe();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStatistics, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hy-AM', {
      style: 'currency',
      currency: 'AMD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const statCards = [
    {
      title: 'Ընդհանուր գրանցումներ',
      value: stats.totalRegistrations,
      icon: FileText,
    },
    {
      title: 'Սպասող գրանցումներ',
      value: stats.pendingRegistrations,
      icon: FileText,
    },
    {
      title: 'Ընդունված գրանցումներ',
      value: stats.approvedRegistrations,
      icon: FileText,
    },
    {
      title: 'Ուսանողներ',
      value: stats.totalStudents,
      icon: Users,
    },
    {
      title: 'Դասախոսներ',
      value: stats.totalLecturers,
      icon: Users,
    },
    {
      title: 'Խմբեր',
      value: stats.totalGroups,
      icon: Users,
    },
    {
      title: 'Ընդհանուր դասեր',
      value: stats.totalLessons,
      icon: BookOpen,
    },
    {
      title: 'Ավարտված դասեր',
      value: stats.completedLessons,
      icon: Calendar,
    },
    {
      title: 'Newsletter բաժանորդներ',
      value: stats.activeSubscribers,
      icon: Mail,
    },
    {
      title: 'Ամսական եկամուտ',
      value: formatCurrency(stats.monthlyRevenue),
      icon: DollarSign,
      isMonetary: true
    },
    {
      title: 'Սպասող աշխատավարձ',
      value: formatCurrency(stats.pendingSalaries),
      icon: DollarSign,
      isMonetary: true
    },
    {
      title: 'Հաստատված աշխատավարձ',
      value: formatCurrency(stats.approvedSalaries),
      icon: DollarSign,
      isMonetary: true
    },
    {
      title: 'Սպասող վարձավճար',
      value: formatCurrency(stats.pendingTuition),
      icon: DollarSign,
      isMonetary: true
    },
    {
      title: 'Հաստատված վարձավճար',
      value: formatCurrency(stats.approvedTuition),
      icon: DollarSign,
      isMonetary: true
    },
    {
      title: 'Ֆինանսական գրանցումներ',
      value: stats.totalFinancialRecords,
      icon: FileText,
    },
    {
      title: 'Ընդհանուր պրոֆիլներ',
      value: stats.totalProfiles,
      icon: Users,
    },
    {
      title: 'Ընդհանուր դասընթացներ',
      value: stats.totalCourses,
      icon: BookOpen,
    },
    {
      title: 'Ակտիվ դասընթացներ',
      value: stats.activeCourses,
      icon: BookOpen,
    },
    {
      title: 'Ընդհանուր ֆակուլտետներ',
      value: stats.totalFaculties,
      icon: BookOpen,
    },
    {
      title: 'Ակտիվ ֆակուլտետներ',
      value: stats.activeFaculties,
      icon: BookOpen,
    }
  ];

  if (loading) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        Բեռնվում է վիճակագրությունը...
      </div>
    );
  }

  const metrics = [
    {
      value: `${stats.totalLessons > 0 ? Math.round((stats.completedLessons / stats.totalLessons) * 100) : 0}%`,
      label: 'Դասերի ավարտման տոկոս',
    },
    {
      value: `${stats.totalRegistrations > 0 ? Math.round((stats.approvedRegistrations / stats.totalRegistrations) * 100) : 0}%`,
      label: 'Գրանցումների ընդունման տոկոս',
    },
    {
      value: stats.totalGroups > 0 ? Math.round(stats.totalStudents / stats.totalGroups) : 0,
      label: 'Միջին ուսանողներ մեկ խմբում',
    },
    {
      value: stats.totalLecturers > 0 && stats.totalGroups > 0 ? Math.round((stats.totalGroups / stats.totalLecturers) * 100) / 100 : 0,
      label: 'Միջին խմբեր մեկ դասախոսի',
    },
  ];

  return (
    <div className="space-y-6">
      <Card className="border-border/70 bg-card/80 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-foreground">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Ընդհանուր վիճակագրություն
            </div>
            <Button
              onClick={fetchStatistics}
              disabled={loading}
              variant="outline"
              size="sm"
              className="cursor-pointer"
              aria-label="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card
                  key={index}
                  className="border-border/70 bg-background/60 transition-colors duration-200 hover:border-primary/50"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="mb-1 text-sm text-muted-foreground">
                          {stat.title}
                        </p>
                        <p className="truncate text-2xl font-bold text-foreground">
                          {stat.isMonetary ? stat.value : (typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value)}
                        </p>
                      </div>
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <Card className="border-border/70 bg-card/80 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <TrendingUp className="h-5 w-5 text-primary" />
            Կատարման ցուցանիշներ
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            {metrics.map((metric) => (
              <Card key={metric.label} className="border-border/70 bg-background/60">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-primary">{metric.value}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{metric.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

